import React, { useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
    Truck,
    Home,
    Hourglass,
    DollarSign,
    Phone,
    MapPin,
    CheckCircle2,
    X,
    MessageCircle,
    Megaphone,
    Copy,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";

/* ──────────────────────────── helpers ──────────────────────────── */

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUS_DOTS = {
    pending_confirmation: { bg: "#C85A17", label: "Pendiente" },
    confirmed: { bg: "#A04711", label: "Confirmado" },
    preparing: { bg: "#D87033", label: "Preparando" },
    ready: { bg: "#4A7C59", label: "Listo" },
    delivered: { bg: "#2C352B", label: "Entregado" },
    cancelled: { bg: "#9CA3AF", label: "Cancelado" },
};

const monthNameES = (d) =>
    d.toLocaleString("es-DO", { month: "long", year: "numeric" });
const dayNameES = (d) =>
    d.toLocaleString("es-DO", { weekday: "short" }).replace(".", "");

/** Convert "2026-05-17" or ISO to a plain YYYY-MM-DD in the local Caribbean timezone */
const toDayKey = (input) => {
    if (!input) return null;
    if (typeof input === "string" && input.length >= 10 && input[4] === "-")
        return input.slice(0, 10);
    try {
        const d = new Date(input);
        if (Number.isNaN(d.getTime())) return null;
        // Format in UTC-4 (Atlantic / DR / Jamaica)
        const tzShift = new Date(d.getTime() - 4 * 60 * 60 * 1000);
        return tzShift.toISOString().slice(0, 10);
    } catch {
        return null;
    }
};

const monthMatrix = (year, monthIdx) => {
    // monthIdx = 0..11
    const first = new Date(year, monthIdx, 1);
    const startDow = first.getDay(); // 0 Sun … 6 Sat
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, monthIdx, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const aggregate = (orders) => {
    const agg = {
        count: orders.length,
        revenue: 0,
        items: {},
        statuses: {},
        hasPreorder: false,
        delivery: 0,
        pickup: 0,
    };
    for (const o of orders) {
        agg.revenue += Number(o.total || 0);
        if (o.has_preorder) agg.hasPreorder = true;
        if (o.delivery_option === "delivery") agg.delivery += 1;
        else agg.pickup += 1;
        agg.statuses[o.status] = (agg.statuses[o.status] || 0) + 1;
        for (const it of o.items || []) {
            agg.items[it.name] = (agg.items[it.name] || 0) + Number(it.quantity || 0);
        }
    }
    return agg;
};

/* ──────────────── broadcast templates ──────────────── */

const BROADCAST_TEMPLATES = [
    {
        key: "confirmed",
        label: "Confirmación",
        emoji: "✅",
        render: ({ name, shortId, dateLabel, total }) =>
            `🇩🇴 ¡Hola ${name}! Te escribimos de *Delicias Dominicanas*. ✅ Tu pedido *#${shortId}* (total: $${total}) está *confirmado* para ${dateLabel}. ¡Pronto te avisamos cuando salga de la cocina! 🌴`,
    },
    {
        key: "preparing",
        label: "Cocinando",
        emoji: "👩‍🍳",
        render: ({ name, shortId, dateLabel }) =>
            `🇩🇴 ¡Hola ${name}! Tu pedido *#${shortId}* de *Delicias Dominicanas* está *en la cocina* 👩‍🍳. Lo entregamos ${dateLabel}. ¡Buen provecho ya casi! 🌴`,
    },
    {
        key: "ready",
        label: "Listo",
        emoji: "📦",
        render: ({ name, shortId, isDelivery }) =>
            isDelivery
                ? `🇩🇴 ¡Hola ${name}! Tu pedido *#${shortId}* de *Delicias Dominicanas* está *listo* 📦 y *en camino* a tu dirección. 🛵`
                : `🇩🇴 ¡Hola ${name}! Tu pedido *#${shortId}* de *Delicias Dominicanas* está *listo para recoger* 📦. ¡Te esperamos! 🏠`,
    },
    {
        key: "reminder",
        label: "Recordatorio",
        emoji: "🔔",
        render: ({ name, shortId, dateLabel, total }) =>
            `🇩🇴 ¡Hola ${name}! Recordatorio amistoso de *Delicias Dominicanas*: tu pedido *#${shortId}* está programado para ${dateLabel}. Total: $${total}. Cualquier ajuste, avísanos por aquí. 🌴`,
    },
    {
        key: "delivered",
        label: "Entregado",
        emoji: "🎉",
        render: ({ name, shortId }) =>
            `🇩🇴 ¡Hola ${name}! Gracias por tu pedido *#${shortId}* 🎉. Esperamos que hayas disfrutado los sabores de *Delicias Dominicanas*. ¡Te esperamos pronto! 🌴 PS: si te gustó, ¡compártenos! 💛`,
    },
];

const autoTemplateForStatus = (status) => {
    if (status === "pending_confirmation") return "confirmed";
    if (status === "confirmed") return "preparing";
    if (status === "preparing") return "ready";
    if (status === "ready") return "reminder";
    if (status === "delivered") return "delivered";
    return "reminder";
};

/* ──────────────────────────── component ──────────────────────────── */

const OrderCalendar = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("delivery"); // delivery | placed
    const [view, setView] = useState("month"); // month | week | list
    const [cursor, setCursor] = useState(() => new Date());
    const [openDay, setOpenDay] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get("/admin/orders", { params: { archived: false } }),
                api.get("/admin/orders", { params: { archived: true } }),
            ]);
            setOrders([...(a.data || []), ...(b.data || [])]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    // Group orders by day for the selected mode
    const byDay = useMemo(() => {
        const map = {};
        for (const o of orders) {
            const key =
                mode === "delivery"
                    ? toDayKey(o.requested_date) || toDayKey(o.created_at)
                    : toDayKey(o.created_at);
            if (!key) continue;
            if (!map[key]) map[key] = [];
            map[key].push(o);
        }
        return map;
    }, [orders, mode]);

    const today = new Date();
    const todayKey = toDayKey(today);

    const shiftCursor = (n) => {
        const c = new Date(cursor);
        if (view === "month") c.setMonth(c.getMonth() + n);
        else if (view === "week") c.setDate(c.getDate() + n * 7);
        else c.setDate(c.getDate() + n * 7);
        setCursor(c);
    };

    /* ──────────────── render ──────────────── */
    if (loading)
        return (
            <div className="bg-white rounded-3xl p-14 text-center text-earth-600 border border-earth-200">
                Cargando calendario…
            </div>
        );

    const totalActive = orders.filter((o) => !o.archived).length;

    return (
        <section data-testid="admin-calendar" className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="font-serif text-3xl font-bold text-earth-900">
                        Calendario de pedidos
                    </h2>
                    <p className="text-sm text-earth-600 mt-1">
                        Planifica cocina y entregas en un vistazo. Click en un día para ver el detalle.
                    </p>
                </div>
                <div className="text-xs text-earth-500">
                    {totalActive} activos · {orders.length} totales
                </div>
            </div>

            {/* Mode + View toggles */}
            <div className="flex flex-wrap items-center gap-3">
                <div
                    data-testid="calendar-mode-toggle"
                    className="inline-flex items-center bg-earth-100 rounded-full p-1"
                >
                    <ToggleBtn
                        active={mode === "delivery"}
                        onClick={() => setMode("delivery")}
                        testId="calendar-mode-delivery"
                    >
                        <Truck className="w-3.5 h-3.5" /> Fecha de entrega
                    </ToggleBtn>
                    <ToggleBtn
                        active={mode === "placed"}
                        onClick={() => setMode("placed")}
                        testId="calendar-mode-placed"
                    >
                        <CalendarDays className="w-3.5 h-3.5" /> Fecha de pedido
                    </ToggleBtn>
                </div>
                <div className="inline-flex items-center bg-earth-100 rounded-full p-1">
                    <ToggleBtn active={view === "month"} onClick={() => setView("month")} testId="calendar-view-month">
                        Mes
                    </ToggleBtn>
                    <ToggleBtn active={view === "week"} onClick={() => setView("week")} testId="calendar-view-week">
                        Semana
                    </ToggleBtn>
                    <ToggleBtn active={view === "list"} onClick={() => setView("list")} testId="calendar-view-list">
                        Lista 7 días
                    </ToggleBtn>
                </div>
                <div className="ml-auto inline-flex items-center gap-1">
                    <button
                        onClick={() => shiftCursor(-1)}
                        data-testid="calendar-prev-btn"
                        className="p-2 rounded-full hover:bg-earth-200"
                        title="Anterior"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setCursor(new Date())}
                        data-testid="calendar-today-btn"
                        className="px-4 py-1.5 rounded-full bg-earth-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-terracotta-500"
                    >
                        Hoy
                    </button>
                    <button
                        onClick={() => shiftCursor(1)}
                        data-testid="calendar-next-btn"
                        className="p-2 rounded-full hover:bg-earth-200"
                        title="Siguiente"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {view === "month" && (
                <MonthView cursor={cursor} byDay={byDay} todayKey={todayKey} onPick={setOpenDay} />
            )}
            {view === "week" && (
                <WeekView cursor={cursor} byDay={byDay} todayKey={todayKey} onPick={setOpenDay} />
            )}
            {view === "list" && (
                <ListView cursor={cursor} byDay={byDay} todayKey={todayKey} onPick={setOpenDay} />
            )}

            {/* Side drawer with day detail */}
            {openDay && (
                <DayDrawer
                    dayKey={openDay}
                    orders={byDay[openDay] || []}
                    mode={mode}
                    onClose={() => setOpenDay(null)}
                    onChanged={load}
                />
            )}
        </section>
    );
};

/* ──────────────────────────── shared bits ──────────────────────────── */

const ToggleBtn = ({ children, active, onClick, testId }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
            active ? "bg-white text-earth-900 shadow-sm" : "text-earth-600 hover:text-earth-900"
        }`}
    >
        {children}
    </button>
);

const DayCellInner = ({ date, agg, isToday, onClick }) => {
    const topItems = Object.entries(agg.items)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
    const totalItems = Object.values(agg.items).reduce((s, n) => s + n, 0);
    return (
        <button
            onClick={onClick}
            data-testid={`calendar-day-${date.toISOString().slice(0, 10)}`}
            className={`group relative w-full h-full min-h-[120px] text-left rounded-2xl border p-2.5 transition-all overflow-hidden ${
                agg.count > 0
                    ? "bg-white border-earth-200 hover:border-terracotta-400 hover:shadow-md"
                    : "bg-earth-50/40 border-earth-100 hover:bg-white"
            } ${isToday ? "ring-2 ring-terracotta-400" : ""}`}
        >
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <div
                    className={`text-sm font-serif font-bold ${
                        isToday ? "text-terracotta-600" : "text-earth-900"
                    }`}
                >
                    {date.getDate()}
                </div>
                {agg.count > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-earth-900 text-white text-[10px] font-bold leading-none">
                        {agg.count}
                    </span>
                )}
            </div>

            {agg.count > 0 && (
                <>
                    <div className="text-[11px] font-bold text-leaf-700 leading-tight mb-1">
                        {fmtMoney(agg.revenue)}
                    </div>
                    {topItems.length > 0 && (
                        <ul className="space-y-0.5 mb-1.5">
                            {topItems.map(([name, qty]) => (
                                <li
                                    key={name}
                                    className="text-[10px] text-earth-700 truncate"
                                    title={`${qty}× ${name}`}
                                >
                                    <b>{qty}×</b> {name}
                                </li>
                            ))}
                            {Object.keys(agg.items).length > 3 && (
                                <li className="text-[10px] text-earth-500 italic">
                                    + {totalItems - topItems.reduce((s, [, q]) => s + q, 0)} más
                                </li>
                            )}
                        </ul>
                    )}
                    {/* status dots row */}
                    <div className="flex items-center gap-1 flex-wrap">
                        {Object.entries(agg.statuses).map(([s, n]) => (
                            <span
                                key={s}
                                title={`${n} ${STATUS_DOTS[s]?.label || s}`}
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase"
                                style={{ color: STATUS_DOTS[s]?.bg || "#888" }}
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: STATUS_DOTS[s]?.bg || "#888" }}
                                />
                                {n}
                            </span>
                        ))}
                        {agg.hasPreorder && (
                            <span
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-blue-700"
                                title="Incluye pre-orden 72h"
                            >
                                <Hourglass className="w-2.5 h-2.5" /> 72h
                            </span>
                        )}
                    </div>
                    {/* delivery/pickup ribbon */}
                    {(agg.delivery > 0 || agg.pickup > 0) && (
                        <div className="absolute bottom-1.5 right-2 flex items-center gap-1 text-[9px] text-earth-500">
                            {agg.delivery > 0 && (
                                <span className="inline-flex items-center gap-0.5">
                                    <Truck className="w-2.5 h-2.5" />
                                    {agg.delivery}
                                </span>
                            )}
                            {agg.pickup > 0 && (
                                <span className="inline-flex items-center gap-0.5">
                                    <Home className="w-2.5 h-2.5" />
                                    {agg.pickup}
                                </span>
                            )}
                        </div>
                    )}
                </>
            )}
        </button>
    );
};

const EmptyDayCell = () => <div className="rounded-2xl bg-transparent" />;

/* ──────────────────────────── Month view ──────────────────────────── */

const MonthView = ({ cursor, byDay, todayKey, onPick }) => {
    const cells = monthMatrix(cursor.getFullYear(), cursor.getMonth());
    return (
        <div className="bg-white rounded-3xl border border-earth-200 p-5 shadow-sm">
            <h3 className="font-serif font-bold text-earth-900 text-xl capitalize mb-4">
                {monthNameES(cursor)}
            </h3>
            <div className="grid grid-cols-7 gap-2 mb-2">
                {["dom", "lun", "mar", "mié", "jue", "vie", "sáb"].map((d) => (
                    <div
                        key={d}
                        className="text-[10px] font-bold uppercase tracking-wider text-earth-500 text-center"
                    >
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
                {cells.map((d, i) =>
                    d === null ? (
                        <EmptyDayCell key={i} />
                    ) : (
                        <DayCellInner
                            key={i}
                            date={d}
                            agg={aggregate(byDay[toDayKey(d)] || [])}
                            isToday={toDayKey(d) === todayKey}
                            onClick={() => onPick(toDayKey(d))}
                        />
                    )
                )}
            </div>
        </div>
    );
};

/* ──────────────────────────── Week view ──────────────────────────── */

const WeekView = ({ cursor, byDay, todayKey, onPick }) => {
    // start of week (Sunday)
    const start = new Date(cursor);
    start.setDate(cursor.getDate() - cursor.getDay());
    const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
    });
    return (
        <div className="bg-white rounded-3xl border border-earth-200 p-5 shadow-sm">
            <h3 className="font-serif font-bold text-earth-900 text-xl mb-4">
                Semana del {start.getDate()} {start.toLocaleString("es-DO", { month: "short" })}
            </h3>
            <div className="grid grid-cols-7 gap-2">
                {days.map((d) => (
                    <div key={d.toISOString()} className="flex flex-col gap-1.5">
                        <div className="text-center">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-earth-500">
                                {dayNameES(d)}
                            </div>
                        </div>
                        <DayCellInner
                            date={d}
                            agg={aggregate(byDay[toDayKey(d)] || [])}
                            isToday={toDayKey(d) === todayKey}
                            onClick={() => onPick(toDayKey(d))}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ──────────────────────────── List view (7 days) ──────────────────────────── */

const ListView = ({ cursor, byDay, todayKey, onPick }) => {
    const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(cursor);
        d.setDate(cursor.getDate() + i);
        return d;
    });
    return (
        <div className="space-y-3">
            {days.map((d) => {
                const key = toDayKey(d);
                const orders = byDay[key] || [];
                const agg = aggregate(orders);
                const isToday = key === todayKey;
                return (
                    <button
                        key={key}
                        onClick={() => onPick(key)}
                        data-testid={`calendar-list-row-${key}`}
                        className={`group w-full bg-white rounded-2xl border p-5 text-left transition-all ${
                            orders.length > 0
                                ? "border-earth-200 hover:border-terracotta-400 hover:shadow-md"
                                : "border-earth-100 opacity-90"
                        } ${isToday ? "ring-2 ring-terracotta-400" : ""}`}
                    >
                        <div className="flex flex-wrap items-start gap-5">
                            <div className="text-center shrink-0 min-w-[64px]">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-earth-500">
                                    {dayNameES(d)}
                                </div>
                                <div
                                    className={`font-serif font-black text-3xl leading-none mt-0.5 ${
                                        isToday ? "text-terracotta-600" : "text-earth-900"
                                    }`}
                                >
                                    {d.getDate()}
                                </div>
                                <div className="text-[10px] text-earth-500 mt-0.5 capitalize">
                                    {d.toLocaleString("es-DO", { month: "short" })}
                                </div>
                            </div>

                            <div className="flex-1 min-w-[200px]">
                                {orders.length === 0 ? (
                                    <div className="text-sm text-earth-500 italic">Sin pedidos</div>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                                            <span className="text-sm font-bold text-earth-900">
                                                {agg.count} pedido{agg.count > 1 ? "s" : ""}
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-sm font-bold text-leaf-700">
                                                <DollarSign className="w-3.5 h-3.5" />
                                                {fmtMoney(agg.revenue)}
                                            </span>
                                            {agg.delivery > 0 && (
                                                <span className="inline-flex items-center gap-1 text-xs text-earth-700">
                                                    <Truck className="w-3 h-3" /> {agg.delivery}
                                                </span>
                                            )}
                                            {agg.pickup > 0 && (
                                                <span className="inline-flex items-center gap-1 text-xs text-earth-700">
                                                    <Home className="w-3 h-3" /> {agg.pickup}
                                                </span>
                                            )}
                                            {agg.hasPreorder && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-blue-700">
                                                    <Hourglass className="w-3 h-3" /> 72h
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                                            {Object.entries(agg.items)
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([name, qty]) => (
                                                    <span
                                                        key={name}
                                                        className="inline-flex items-center gap-1 text-xs text-earth-700"
                                                    >
                                                        <b className="text-earth-900">{qty}×</b> {name}
                                                    </span>
                                                ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex flex-col items-end gap-1 shrink-0">
                                {Object.entries(agg.statuses).map(([s, n]) => (
                                    <span
                                        key={s}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
                                        style={{ color: STATUS_DOTS[s]?.bg || "#888" }}
                                    >
                                        <span
                                            className="w-1.5 h-1.5 rounded-full"
                                            style={{ background: STATUS_DOTS[s]?.bg || "#888" }}
                                        />
                                        {n} {STATUS_DOTS[s]?.label || s}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

/* ──────────────────────────── Day drawer ──────────────────────────── */

const DayDrawer = ({ dayKey, orders, mode, onClose, onChanged }) => {
    const agg = aggregate(orders);
    const d = new Date(`${dayKey}T12:00:00`);
    const dateLabel = d.toLocaleString("es-DO", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    const dateShort = d.toLocaleString("es-DO", { weekday: "long", day: "numeric", month: "long" });
    const [showBroadcast, setShowBroadcast] = useState(false);
    const [templateKey, setTemplateKey] = useState(null); // null = auto per-order
    const eligible = orders.filter(
        (o) => o.status !== "cancelled" && (o.customer_phone || "").replace(/\D/g, "").length >= 7
    );

    const messageFor = (o) => {
        const tpl =
            BROADCAST_TEMPLATES.find(
                (x) => x.key === (templateKey || autoTemplateForStatus(o.status))
            ) || BROADCAST_TEMPLATES[0];
        return tpl.render({
            name: o.customer_name || "",
            shortId: o.id.slice(0, 8).toUpperCase(),
            dateLabel: dateShort,
            total: Number(o.total || 0).toFixed(2),
            isDelivery: o.delivery_option === "delivery",
        });
    };
    const waLinkFor = (o) => {
        const digits = (o.customer_phone || "").replace(/\D/g, "");
        return `https://wa.me/${digits}?text=${encodeURIComponent(messageFor(o))}`;
    };

    const confirm = async (id) => {
        try {
            const r = await api.post(`/admin/orders/${id}/confirm`);
            toast.success(
                <span>
                    Pedido confirmado.{" "}
                    {r.data?.customer_whatsapp_url && (
                        <a
                            href={r.data.customer_whatsapp_url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline font-bold"
                        >
                            Enviar WhatsApp →
                        </a>
                    )}
                </span>
            );
            onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };
    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/orders/${id}`, { status });
            toast.success("Estado actualizado");
            onChanged();
        } catch {
            toast.error("Error");
        }
    };
    const markPaid = async (id) => {
        try {
            await api.patch(`/admin/orders/${id}`, { payment_status: "paid" });
            toast.success("Marcado como pagado");
            onChanged();
        } catch {
            toast.error("Error");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            {/* backdrop */}
            <button
                aria-label="Cerrar"
                onClick={onClose}
                className="flex-1 bg-earth-900/60 backdrop-blur-sm"
            />
            {/* panel */}
            <aside
                data-testid="calendar-day-drawer"
                className="w-full sm:w-[560px] max-w-[100vw] h-full bg-earth-50 shadow-2xl overflow-y-auto flex flex-col"
            >
                {/* header */}
                <div className="sticky top-0 z-10 bg-white border-b border-earth-200 px-6 py-5">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-wider font-bold text-earth-500">
                                {mode === "delivery" ? "Entrega · " : "Pedido · "}
                                {agg.count} pedido{agg.count !== 1 ? "s" : ""}
                            </div>
                            <h3 className="font-serif font-bold text-earth-900 text-xl capitalize leading-tight mt-0.5">
                                {dateLabel}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="calendar-drawer-close-btn"
                            className="p-2 rounded-full hover:bg-earth-100"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    {/* aggregate KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                        <KPI label="Ingresos" value={fmtMoney(agg.revenue)} />
                        <KPI label="Delivery" value={agg.delivery} icon={Truck} />
                        <KPI label="Pickup" value={agg.pickup} icon={Home} />
                        <KPI
                            label="Pre-orden 72h"
                            value={agg.hasPreorder ? "Sí" : "—"}
                            icon={Hourglass}
                        />
                    </div>
                    {/* what to cook */}
                    {Object.keys(agg.items).length > 0 && (
                        <div className="mt-4 p-3 rounded-2xl bg-leaf-500/10 border border-leaf-500/30">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-leaf-700 mb-1.5">
                                Plan de cocina
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                                {Object.entries(agg.items)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([name, qty]) => (
                                        <span
                                            key={name}
                                            className="inline-flex items-center gap-1 text-sm text-earth-800"
                                        >
                                            <b className="text-leaf-700">{qty}×</b> {name}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                    {/* WhatsApp broadcast */}
                    {eligible.length > 0 && (
                        <div className="mt-4">
                            <button
                                data-testid="broadcast-toggle-btn"
                                onClick={() => setShowBroadcast((s) => !s)}
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-bold transition-colors shadow-sm"
                            >
                                <Megaphone className="w-4 h-4" />
                                {showBroadcast
                                    ? "Cerrar avisos por WhatsApp"
                                    : `Avisar a ${eligible.length} cliente${eligible.length !== 1 ? "s" : ""} por WhatsApp`}
                            </button>
                            {showBroadcast && (
                                <BroadcastPanel
                                    orders={eligible}
                                    templateKey={templateKey}
                                    setTemplateKey={setTemplateKey}
                                    messageFor={messageFor}
                                    waLinkFor={waLinkFor}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* orders list */}
                <div className="flex-1 px-6 py-5 space-y-4">
                    {orders.length === 0 ? (
                        <div className="text-center text-earth-500 italic py-12">
                            No hay pedidos para este día.
                        </div>
                    ) : (
                        orders
                            .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
                            .map((o) => (
                                <article
                                    key={o.id}
                                    data-testid={`drawer-order-${o.id}`}
                                    className={`bg-white rounded-2xl p-4 border-2 ${
                                        o.status === "pending_confirmation"
                                            ? "border-terracotta-400"
                                            : "border-earth-200"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div>
                                            <div className="font-serif font-bold text-earth-900">
                                                #{o.id.slice(0, 8).toUpperCase()}
                                            </div>
                                            <div className="text-xs text-earth-500">
                                                {new Date(o.created_at).toLocaleString()}
                                            </div>
                                        </div>
                                        <span
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                                            style={{ background: STATUS_DOTS[o.status]?.bg || "#888" }}
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                            {STATUS_DOTS[o.status]?.label || o.status}
                                        </span>
                                    </div>

                                    <div className="text-sm font-semibold text-earth-900">
                                        {o.customer_name}
                                    </div>
                                    <a
                                        href={`https://wa.me/${(o.customer_phone || "").replace(/\D/g, "")}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-terracotta-600 hover:underline inline-flex items-center gap-1"
                                    >
                                        <Phone className="w-3 h-3" />
                                        {o.customer_phone}
                                    </a>
                                    {o.delivery_option === "delivery" ? (
                                        <div className="text-xs text-earth-700 mt-1 inline-flex items-start gap-1">
                                            <Truck className="w-3 h-3 mt-0.5" />
                                            {o.delivery_address || "Delivery"}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-earth-700 mt-1 inline-flex items-center gap-1">
                                            <Home className="w-3 h-3" /> Pickup
                                        </div>
                                    )}
                                    {o.has_preorder && (
                                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700">
                                            <Hourglass className="w-3 h-3" /> Pre-orden 72h
                                        </div>
                                    )}

                                    <ul className="mt-3 space-y-0.5 text-xs">
                                        {o.items.map((it) => (
                                            <li key={it.item_id} className="flex justify-between text-earth-800">
                                                <span>
                                                    {it.quantity}× {it.name}
                                                </span>
                                                <span className="font-semibold">
                                                    {fmtMoney(it.price * it.quantity)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-earth-100">
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-earth-500">
                                            Total
                                        </span>
                                        <span className="font-serif font-black text-terracotta-600 text-lg">
                                            {fmtMoney(o.total)}
                                        </span>
                                    </div>

                                    {/* quick actions */}
                                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-earth-100">
                                        {o.status === "pending_confirmation" && (
                                            <button
                                                onClick={() => confirm(o.id)}
                                                data-testid={`drawer-confirm-${o.id}-btn`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white text-[10px] font-bold uppercase tracking-wider"
                                            >
                                                <CheckCircle2 className="w-3 h-3" /> Confirmar
                                            </button>
                                        )}
                                        {["preparing", "ready", "delivered"].map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setStatus(o.id, s)}
                                                data-testid={`drawer-status-${s}-${o.id}-btn`}
                                                className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    o.status === s
                                                        ? "bg-earth-900 text-white"
                                                        : "bg-earth-100 hover:bg-earth-200 text-earth-700"
                                                }`}
                                            >
                                                {STATUS_DOTS[s]?.label || s}
                                            </button>
                                        ))}
                                        {o.payment_status !== "paid" && (
                                            <button
                                                onClick={() => markPaid(o.id)}
                                                data-testid={`drawer-paid-${o.id}-btn`}
                                                className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-leaf-500 hover:bg-leaf-600 text-white text-[10px] font-bold uppercase tracking-wider"
                                            >
                                                <DollarSign className="w-3 h-3" /> Marcar pagado
                                            </button>
                                        )}
                                    </div>
                                </article>
                            ))
                    )}
                </div>
            </aside>
        </div>
    );
};

const KPI = ({ label, value, icon: Icon }) => (
    <div className="rounded-xl bg-earth-100 p-2.5 text-center">
        <div className="text-[10px] uppercase tracking-wider font-bold text-earth-500 inline-flex items-center justify-center gap-1">
            {Icon && <Icon className="w-3 h-3" />}
            {label}
        </div>
        <div className="font-serif font-bold text-earth-900 mt-0.5">{value}</div>
    </div>
);

/* ──────────────────────────── Broadcast panel ──────────────────────────── */

const BroadcastPanel = ({ orders, templateKey, setTemplateKey, messageFor, waLinkFor }) => {
    const [sent, setSent] = useState({});
    const [copied, setCopied] = useState(null);

    const markSent = (id) =>
        setSent((s) => ({ ...s, [id]: true }));
    const copyMsg = async (o) => {
        try {
            await navigator.clipboard.writeText(messageFor(o));
            setCopied(o.id);
            setTimeout(() => setCopied((c) => (c === o.id ? null : c)), 1500);
        } catch (err) {
            console.warn("Clipboard write failed:", err);
        }
    };

    return (
        <div
            data-testid="broadcast-panel"
            className="mt-3 rounded-2xl border-2 border-[#25D366]/40 bg-white p-4"
        >
            <div className="text-[10px] uppercase tracking-wider font-bold text-earth-500 mb-2">
                Plantilla del mensaje
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
                <button
                    onClick={() => setTemplateKey(null)}
                    data-testid="broadcast-tpl-auto"
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                        !templateKey
                            ? "bg-earth-900 text-white"
                            : "bg-earth-100 hover:bg-earth-200 text-earth-700"
                    }`}
                >
                    Auto (por estado)
                </button>
                {BROADCAST_TEMPLATES.map((tpl) => (
                    <button
                        key={tpl.key}
                        onClick={() => setTemplateKey(tpl.key)}
                        data-testid={`broadcast-tpl-${tpl.key}`}
                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            templateKey === tpl.key
                                ? "bg-earth-900 text-white"
                                : "bg-earth-100 hover:bg-earth-200 text-earth-700"
                        }`}
                    >
                        {tpl.emoji} {tpl.label}
                    </button>
                ))}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-earth-500 mb-2">
                {orders.length} cliente{orders.length !== 1 ? "s" : ""}
            </div>
            <ul className="space-y-2">
                {orders.map((o) => (
                    <li
                        key={o.id}
                        data-testid={`broadcast-row-${o.id}`}
                        className={`flex items-start gap-2 p-2.5 rounded-xl border ${
                            sent[o.id]
                                ? "border-leaf-300 bg-leaf-50/60"
                                : "border-earth-200 bg-earth-50/40"
                        }`}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-earth-900 truncate">
                                {o.customer_name}{" "}
                                <span className="text-earth-500 font-normal">
                                    · #{o.id.slice(0, 8).toUpperCase()}
                                </span>
                            </div>
                            <div className="text-[10px] text-earth-600">{o.customer_phone}</div>
                            <div className="text-[10px] text-earth-700 mt-1 italic line-clamp-2">
                                "{messageFor(o)}"
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                            <a
                                href={waLinkFor(o)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => markSent(o.id)}
                                data-testid={`broadcast-send-${o.id}-btn`}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                    sent[o.id]
                                        ? "bg-leaf-500 text-white"
                                        : "bg-[#25D366] hover:bg-[#1ebe5d] text-white"
                                }`}
                            >
                                {sent[o.id] ? (
                                    <>
                                        <CheckCircle2 className="w-3 h-3" /> Enviado
                                    </>
                                ) : (
                                    <>
                                        <MessageCircle className="w-3 h-3" /> Enviar
                                    </>
                                )}
                            </a>
                            <button
                                onClick={() => copyMsg(o)}
                                data-testid={`broadcast-copy-${o.id}-btn`}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-700 text-[10px] font-bold uppercase tracking-wider"
                            >
                                <Copy className="w-3 h-3" />
                                {copied === o.id ? "¡Copiado!" : "Copiar"}
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            <p className="text-[10px] text-earth-500 italic mt-2">
                Al pulsar Enviar se abre WhatsApp con el mensaje listo. Tocas el botón verde de
                WhatsApp para enviarlo y volvés acá.
            </p>
        </div>
    );
};

export default OrderCalendar;
